import { LockOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { Dropdown } from 'antd';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { confirmLogout } from '@/utils/confirmDialog';
import styles from './UserProfileDropdown.module.scss';

interface UserProfileDropdownProps {
  children: ReactNode | ((open: boolean) => ReactNode);
  userName: string;
  userEmail: string;
  userRole?: string;
  avatarUrl?: string;
  theme?: 'light' | 'dark';
  onSignOut: () => void;
}

export function UserProfileDropdown({
  children,
  userName,
  userEmail,
  userRole,
  avatarUrl,
  theme = 'light',
  onSignOut,
}: UserProfileDropdownProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const closeAndNavigate = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const openPasswordTab = () => {
    setOpen(false);
    navigate(`${ROUTES.PROFILE}?tab=password`);
  };

  const handleSignOut = async () => {
    setOpen(false);
    const confirmed = await confirmLogout(theme);
    if (confirmed) {
      onSignOut();
    }
  };

  const popup = (
    <div className={styles.panel} data-theme={theme}>
      <div className={styles.panelAccent} />

      <div className={styles.header}>
        <div className={styles.avatarRing}>
          <PreviewImage
            src={avatarUrl}
            alt={userName}
            size={52}
            shape="circle"
            fallback={userName.charAt(0).toUpperCase()}
          />
        </div>
        <div className={styles.userInfo}>
          <span className={styles.name}>{userName}</span>
          <span className={styles.email}>{userEmail}</span>
          {userRole ? <span className={styles.roleBadge}>{userRole}</span> : null}
        </div>
      </div>

      <div className={styles.menuGroup}>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => closeAndNavigate(ROUTES.PROFILE)}
        >
          <span className={`${styles.menuIconWrap} ${styles.menuIconGold}`}>
            <UserOutlined />
          </span>
          <span className={styles.menuText}>
            <strong>Update profile</strong>
            <small>View and edit your details</small>
          </span>
        </button>

        <button type="button" className={styles.menuItem} onClick={openPasswordTab}>
          <span className={`${styles.menuIconWrap} ${styles.menuIconBlue}`}>
            <LockOutlined />
          </span>
          <span className={styles.menuText}>
            <strong>Update password</strong>
            <small>Change your account password</small>
          </span>
        </button>
      </div>

      <div className={styles.divider} />

      <button
        type="button"
        className={`${styles.menuItem} ${styles.menuItemDanger}`}
        onClick={handleSignOut}
      >
        <span className={`${styles.menuIconWrap} ${styles.menuIconRed}`}>
          <LogoutOutlined />
        </span>
        <span className={styles.menuText}>
          <strong>Logout</strong>
        </span>
      </button>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => popup}
    >
      {typeof children === 'function' ? children(open) : children}
    </Dropdown>
  );
}
